import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { APP_LOCAL_STORAGE_KEY, APP_STORE } from '@/constants/app.constant'
import { PERMISSION_CONFIG } from '@/constants/permissions.constant'
import type { User, TokenData } from '@/types'

export const useAuthStore = defineStore(
  APP_STORE.AUTH,
  () => {
    const user = ref<User | null>(null)
    const token = ref<TokenData | null>(null)

    const isAuthenticated = computed(() => !!token.value?.token)

    const permissions = computed((): string[] => {
      if (user.value?.superUser) {
        return Object.values(PERMISSION_CONFIG)
      }

      const role = user.value?.role
      const perms = role?.permissions || []
      return Array.from(new Set(perms)) as string[]
    })

    const hasPermission = (permission: string): boolean => {
      return permissions.value.includes(permission)
    }

    const hasRole = (roleNames: string[]): boolean => {
      return roleNames.includes(user.value?.role?.name || '')
    }

    const hasPermissions = (requiredPermissions: string[]): boolean => {
      return requiredPermissions.every(perm => hasPermission(perm))
    }

    const setUser = (userData: User | null) => {
      user.value = userData
    }

    const setToken = (tokenData: TokenData | null) => {
      token.value = tokenData
    }

    const setAuthData = (userData: User, tokenData: TokenData) => {
      user.value = userData
      token.value = tokenData
      localStorage.setItem(APP_LOCAL_STORAGE_KEY.TOKEN_KEY, tokenData.token)
      localStorage.setItem(APP_LOCAL_STORAGE_KEY.USER_KEY, JSON.stringify(userData))
    }

    const clearAuth = () => {
      user.value = null
      token.value = null
      localStorage.removeItem(APP_LOCAL_STORAGE_KEY.TOKEN_KEY)
      localStorage.removeItem(APP_LOCAL_STORAGE_KEY.USER_KEY)
    }

    const logout = () => {
      clearAuth()
    }

    return {
      user,
      token,
      isAuthenticated,
      permissions,
      hasPermission,
      hasRole,
      hasPermissions,
      setUser,
      setToken,
      setAuthData,
      clearAuth,
      logout
    }
  },
  {
    persist: true,
  }
)
